// Gamification types for achievements, levels, and streaks

export type AchievementCategory = 'goals' | 'savings' | 'streaks' | 'milestones' | 'engagement'

export type AchievementId = 
  // Goals
  | 'first_steps'
  | 'goal_getter'
  | 'triple_threat'
  | 'goal_master'
  | 'dream_big'
  // Savings
  | 'first_dollar'
  | 'century_club'
  | 'thousand_strong'
  | 'five_figure_saver'
  | 'six_figure_club'
  // Streaks
  | 'week_warrior'
  | 'monthly_momentum'
  | 'quarterly_champion'
  | 'year_of_dedication'
  // Milestones
  | 'emergency_ready'
  | 'debt_destroyer'
  | 'homeward_bound'
  | 'early_bird'
  // Engagement
  | 'explorer'
  | 'data_driven'
  | 'planner_pro'

export interface Achievement {
  id: AchievementId
  name: string
  description: string
  category: AchievementCategory
  icon: string              // Lucide icon name
  points: number
  unlockedAt: string | null // ISO date when earned, null if locked
  progress: number          // Current progress value
  requirement: number       // Target for progressive achievements
}

export interface GamificationState {
  level: number
  currentXP: number
  totalXP: number
  achievements: Record<AchievementId, Achievement>
  currentStreak: number     // Days of consecutive activity
  longestStreak: number
  lastActivityDate: string | null
  weeklyGoalContributions: number // Count for weekly activity
  visitedPages: string[]    // For explorer achievement
}

// Level definitions
export interface LevelInfo {
  level: number
  xpRequired: number
  title: string
}

export const LEVEL_DEFINITIONS: LevelInfo[] = [
  { level: 1, xpRequired: 0, title: 'Beginner' },
  { level: 2, xpRequired: 100, title: 'Saver' },
  { level: 3, xpRequired: 300, title: 'Planner' },
  { level: 4, xpRequired: 600, title: 'Achiever' },
  { level: 5, xpRequired: 1000, title: 'Expert' },
  { level: 6, xpRequired: 1500, title: 'Master' },
  { level: 7, xpRequired: 2100, title: 'Champion' },
  { level: 8, xpRequired: 2800, title: 'Legend' },
  { level: 9, xpRequired: 3600, title: 'Elite' },
  { level: 10, xpRequired: 4500, title: 'Financial Guru' },
]

// Helper to get level info from XP
export function getLevelFromXP(totalXP: number): LevelInfo {
  for (let i = LEVEL_DEFINITIONS.length - 1; i >= 0; i--) {
    if (totalXP >= LEVEL_DEFINITIONS[i].xpRequired) {
      return LEVEL_DEFINITIONS[i]
    }
  }
  return LEVEL_DEFINITIONS[0]
}

// Helper to get XP needed for next level
export function getXPToNextLevel(totalXP: number): { current: number; required: number; nextLevel: LevelInfo | null } {
  const currentLevel = getLevelFromXP(totalXP)
  const nextLevelIndex = LEVEL_DEFINITIONS.findIndex(l => l.level === currentLevel.level) + 1
  
  if (nextLevelIndex >= LEVEL_DEFINITIONS.length) {
    return { current: totalXP, required: totalXP, nextLevel: null }
  }
  
  const nextLevel = LEVEL_DEFINITIONS[nextLevelIndex]
  const currentLevelXP = currentLevel.xpRequired
  const xpInCurrentLevel = totalXP - currentLevelXP
  const xpNeededForNextLevel = nextLevel.xpRequired - currentLevelXP
  
  return {
    current: xpInCurrentLevel,
    required: xpNeededForNextLevel,
    nextLevel
  }
}

// Achievement category colors
export const ACHIEVEMENT_CATEGORY_COLORS: Record<AchievementCategory, string> = {
  goals: 'text-blue-500 bg-blue-500/10',
  savings: 'text-green-500 bg-green-500/10',
  streaks: 'text-orange-500 bg-orange-500/10',
  milestones: 'text-purple-500 bg-purple-500/10',
  engagement: 'text-cyan-500 bg-cyan-500/10',
}
